import * as React from "react";
import { numberBoxText, typedMatchesValue } from "./numberInput.logic";

/**
 * A plain `<input>` whose NUMBER box can be emptied and doesn't show a zero.
 *
 * Drop-in for `<input>`: same props, same ref, no styling. Only a controlled
 * `type="number"` box (one with a `value`) behaves differently — at rest a 0
 * reads as an empty box, and while someone types it shows what they typed
 * instead of the 0 / minimum the form turns an empty box into. The value and
 * the change events reaching the form are untouched. Rules and reasons:
 * numberInput.logic.ts. `Input` (ui/input.tsx) renders through this, so every
 * shadcn number box gets it; a bare number `<input>` swaps its tag for this.
 *
 * An uncontrolled box (react-hook-form `register`) passes straight through:
 * the form reads the box's own text on blur, so blanking a 0 there would
 * change what gets saved. Those forms start empty in their defaults instead.
 */
export type NumberInputProps = React.ComponentProps<"input"> & {
  /** Show a resting 0 as "0": for a box where 0 is a choice, not "nothing yet"
   *  (a condition value, an offset in days). See restingNumberText. */
  keepZero?: boolean;
};

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChange, onBlur, type, keepZero = false, ...props }, ref) => {
    const [typed, setTyped] = React.useState<string | null>(null);
    // The value the form gave back after the last keystroke — its answer to
    // what was typed. `waiting` until the commit after that keystroke.
    const answer = React.useRef<{ waiting: boolean; value: unknown }>({ waiting: false, value: undefined });

    const managed = type === "number" && value !== undefined;
    const answered = answer.current.waiting || Object.is(answer.current.value, value);

    React.useLayoutEffect(() => {
      if (!managed) return;
      if (answer.current.waiting) {
        answer.current = { waiting: false, value };
        return;
      }
      // Changed for some other reason (a reset, a fill button): the form wins.
      if (typed !== null && !answered && !typedMatchesValue(typed, value)) setTyped(null);
    });

    if (!managed) {
      return <input ref={ref} type={type} value={value} onChange={onChange} onBlur={onBlur} {...props} />;
    }

    return (
      <input
        ref={ref}
        type={type}
        value={numberBoxText(value, typed, answered, keepZero)}
        onChange={(e) => {
          setTyped(e.target.value);
          answer.current = { waiting: true, value: undefined };
          onChange?.(e);
        }}
        onBlur={(e) => {
          setTyped(null);
          answer.current = { waiting: false, value: undefined };
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);
NumberInput.displayName = "NumberInput";
